import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { generateProfile, generateCourse } from '@/test/data-generators';
import { db } from '@/test/mocks/db';
import { networkDelay, requireAuth } from '@/test/mocks/utils';

import { ProfilesResponse } from '../types';

// Mock state to track selected profile and variant across API calls
export const mockProfileState = {
  selectedProfileId: null as string | null,
  selectedVariantId: null as string | null,

  // Helper methods for managing state
  setSelectedProfile: (profileId: string) => {
    mockProfileState.selectedProfileId = profileId;
  },

  clearSelectedProfile: () => {
    mockProfileState.selectedProfileId = null;
  },

  getSelectedProfile: () => mockProfileState.selectedProfileId,

  setSelectedVariant: (variantId: string) => {
    mockProfileState.selectedVariantId = variantId;
  },

  clearSelectedVariant: () => {
    mockProfileState.selectedVariantId = null;
  },

  getSelectedVariant: () => mockProfileState.selectedVariantId,
};

export const getProfiles = (): Promise<ProfilesResponse.Root> => {
  return axios.get(`/user/v1/profiles`);
};

export const getProfilesMockHandler = http.get(
  `${env.API_URL}/user/v1/profiles`,
  async ({ cookies }): Promise<HttpResponse<ProfilesResponse.Root>> => {
    await networkDelay();
    const { error } = requireAuth(cookies);
    if (error) {
      return HttpResponse.json(
        {
          message: error,
          consent_granted: false,
          logged_in_user: '',
          primary_profile: '',
          profiles: [],
        },
        { status: 401 }
      );
    }

    // Get all profiles from the database
    const profiles = db.profile.getAll();
    const users = db.user.getAll();

    // Find the first user (in real app, this would be based on auth token)
    const [currentUser] = users;
    const userProfiles = profiles.filter(
      profile => profile.base_lead_id === currentUser?.base_lead_id
    );

    if (userProfiles.length === 0 && currentUser) {
      const course1 = generateCourse({
        status: 'ACTIVE',
        batch_id: faker.datatype.boolean() ? faker.string.uuid() : '',
      });

      const course2 = generateCourse({
        status: 'ACTIVE',
        course_name: 'Grade 3 Academic Maths ',
        category: 'on_demand',
        batch_id: faker.datatype.boolean() ? faker.string.uuid() : '',
      });
      const course3 = generateCourse({
        status: 'ACTIVE',
        course_name: 'Grade 5 Academic Maths',
        category: 'live',
      });

      const generatedCourse1 = db.course.create(course1);
      const generatedCourse2 = db.course.create(course2);
      const generatedCourse3 = db.course.create(course3);

      // Create first student profile using data generator
      const studentProfileData1 = generateProfile({
        email: currentUser.email,
        role: 'STUDENT',
        base_lead_id: currentUser.base_lead_id,
        practice_id: currentUser.practice_id,
        doubtgpt_enabled: true,
        first_registered: [],
      });

      // Create the first student profile with enrolled courses
      const studentProfile1 = db.profile.create({
        ...studentProfileData1,
        enrolled_courses: [generatedCourse1, generatedCourse2],
        selected_variant_id: generatedCourse1.variant_id,
      } as Parameters<typeof db.profile.create>[0]);

      // Create second student profile using data generator
      const studentProfileData2 = generateProfile({
        email: currentUser.email,
        role: 'STUDENT',
        base_lead_id: currentUser.base_lead_id,
        practice_id: currentUser.practice_id,
        doubtgpt_enabled: true,
      });

      // Create the second student profile with enrolled courses
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const studentProfile2 = db.profile.create({
        ...studentProfileData2,
        enrolled_courses: [generatedCourse3],
        selected_variant_id: generatedCourse3.variant_id,
      } as any);

      // Create third student profile using data generator
      const studentProfileData3 = generateProfile({
        email: currentUser.email,
        role: 'STUDENT',
        base_lead_id: currentUser.base_lead_id,
        practice_id: currentUser.practice_id,
        doubtgpt_enabled: false,
      });

      // Create the third student profile with enrolled courses
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const studentProfile3 = db.profile.create({
        ...studentProfileData3,
        enrolled_courses: [generatedCourse1],
        selected_variant_id: generatedCourse1.variant_id,
      } as any);

      // Create a parent profile using data generator
      const parentProfileData = generateProfile({
        email: currentUser.email,
        role: 'PARENT',
        base_lead_id: currentUser.base_lead_id,
        practice_id: currentUser.practice_id,
        grade: undefined,
        doubtgpt_enabled: false,
        // avoid passing plain objects for relations
        first_registered: [],
      });

      // Create the parent profile with empty enrolled courses
      const parentProfilePayload: Parameters<typeof db.profile.create>[0] = {
        ...parentProfileData,
        first_registered: [],
        enrolled_courses: [],
      };

      const parentProfile = db.profile.create(parentProfilePayload);

      userProfiles.push(
        studentProfile1,
        studentProfile2,
        studentProfile3,
        parentProfile
      );
    }

    // Transform profiles to match the expected format
    const transformedProfiles: ProfilesResponse.Profile[] = userProfiles.map(
      profile => ({
        ...profile,
        role: profile.role as 'PARENT' | 'STUDENT',
      })
    );

    return HttpResponse.json(
      // profileMockData as unknown as ProfilesResponse.Root
      // uncomment this to use the mock data
      {
        status: 'Success',
        message: 'Profiles retrieved successfully',
        profiles: transformedProfiles,
        logged_in_user: currentUser?.id || '',
        primary_profile: transformedProfiles[0]?.id || '',
        selected_profile: mockProfileState.selectedProfileId ?? undefined,
        selected_variant_id:
          mockProfileState.selectedVariantId ??
          transformedProfiles[0]?.selected_variant_id ??
          '',
        consent_granted: true,
      }
    );
  }
);
